/**
 * Stable keys identifying a changed plan field in a collab wizard.
 *
 * These are opaque navigation handles, not wire or property names: a revision
 * chip carries one, and the plan panel reveals whichever field anchors it. That
 * is why they stayed snake_case through the camelCase migration — renaming them
 * would have been churn with a chance of breaking chip-to-field matching.
 *
 * Every key below is entity-agnostic. An entity's `planFreeTextDiff` spreads
 * these and adds the two or three that name what it builds.
 */
export const collabFieldKeys = {
  attribute: (reference: string) => `attr:${reference}`,
  tableName: (tableName: string) => `table:${tableName}:name`,
  tablePurpose: (tableName: string) => `table:${tableName}:purpose`,
  tableTestingContext: (tableName: string) => `table:${tableName}:tc`,
  tableDocStandards: (tableName: string) => `table:${tableName}:ds`,
  tableAttributeRefs: (tableName: string) => `table:${tableName}:refs`,
  colName: (tableName: string, columnKey: string) =>
    `col:${tableName}:${columnKey}:name`,
  colPass: (tableName: string, columnKey: string) =>
    `col:${tableName}:${columnKey}:pass`,
  colFail: (tableName: string, columnKey: string) =>
    `col:${tableName}:${columnKey}:fail`,
  colNa: (tableName: string, columnKey: string) =>
    `col:${tableName}:${columnKey}:na`,
  colGuidance: (tableName: string, columnKey: string) =>
    `col:${tableName}:${columnKey}:pg`,
  colTolerances: (tableName: string, columnKey: string) =>
    `col:${tableName}:${columnKey}:tol`,
  colExpectedEvidences: (tableName: string, columnKey: string) =>
    `col:${tableName}:${columnKey}:ee`,
  supportName: (tableName: string, columnName: string) =>
    `support:${tableName}:${columnName}:name`,
  supportDescription: (tableName: string, columnName: string) =>
    `support:${tableName}:${columnName}:desc`,
  // Keyed by name: a proposed analytics column has no id until it is saved.
  analyticsName: (tableName: string, columnName: string) =>
    `analytics:${tableName}:${columnName}:name`,
  analyticsPass: (tableName: string, columnName: string) =>
    `analytics:${tableName}:${columnName}:pass`,
  analyticsFail: (tableName: string, columnName: string) =>
    `analytics:${tableName}:${columnName}:fail`,
  analyticsNa: (tableName: string, columnName: string) =>
    `analytics:${tableName}:${columnName}:na`,
  analyticsGuidance: (tableName: string, columnName: string) =>
    `analytics:${tableName}:${columnName}:pg`,
  analyticsTolerances: (tableName: string, columnName: string) =>
    `analytics:${tableName}:${columnName}:tol`,
  analyticsExpectedEvidences: (tableName: string, columnName: string) =>
    `analytics:${tableName}:${columnName}:ee`,
  analyticsDraftedTest: (tableName: string, columnName: string) =>
    `analytics:${tableName}:${columnName}:dt`,
};
